import { useEffect } from 'react';
import classNames from 'classnames';
import { IconClinicalTrials } from '@medifind/icons';
import { conditionAutocomplete } from '@medifind/interface';
import { StoreFilterAutocomplete } from '@medifind/search-results';
import { updateTrialConditionFilterOption, useClinicalTrialStepper, useTrialConditionFilter } from '@medifind/zustand';
import Step from './Step';
import styles from './StepShared.module.scss';

const ConditionStep = ({ stepKey, handleStepSubmit, showOptional, trialCount, loading }) => {
  const trialCondition = useTrialConditionFilter();
  const steps = useClinicalTrialStepper((state) => state.steps);
  const conditionStep = steps.find(({ key }) => key === stepKey);
  const { data, skip } = conditionStep || {};

  const handleConditionSelected = (selection) => {
    if (selection) {
      const { display, value } = selection;
      handleStepSubmit({ data: { display, value } });
    }
  };

  const handleClearSelected = (...args) => {
    handleStepSubmit({ doNotAdvance: true, data: { display: '', value: '' } });
  };

  useEffect(() => {
    if (data?.value !== trialCondition?.selected?.value) {
      return updateTrialConditionFilterOption(data);
    }
  }, [data?.value, trialCondition?.selected?.value]);

  const handleAutoComplete = async (input) => {
    try {
      const { results } = await conditionAutocomplete({ input, showAll: true, size: 200 });
      return results.map(({ display, codingType, projectId, condition }) => ({
        display: display,
        value: projectId,
        condition: condition,
        codingType: codingType,
      }));
    } catch (e) {
      return [
        {
          display: 'Type a few more characters',
          value: '',
          condition: '',
          codingType: '',
        },
      ];
    }
  };

  return (
    <Step
      title="What is your condition?"
      icon={<IconClinicalTrials width={100} height={100} color="#4062BB" showBoxShadow={false} showBackground={false} />}
      optionsColStyles={styles['condition-step']}
      showNext={skip || data?.value}
      showOptional={showOptional}
      trialCount={trialCount}
      showTrialCount
      loading={loading}
    >
      <StoreFilterAutocomplete
        filter={trialCondition}
        label="Condition"
        placeholder="Search for a condition or procedure..."
        onSelect={handleConditionSelected}
        autocompleteFunction={handleAutoComplete}
        prepopulateOnFocus
        actLikeASelect
        showClearButton
        noHeader
        classes={{
          header: styles['headers'],
          label: styles['label'],
          what: styles['what'],
          inputWrapper: styles['inputs-wrapper'],
          menuWrapper: styles['text'],
          menuItem: styles['text'],
          menu: styles['menu'],
          menuList: styles['menu-list'],
          input: classNames(styles['text'], styles['condition-input']),
        }}
        onClear={handleClearSelected}
      />
    </Step>
  );
};

export default ConditionStep;
